import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BASE_CURRENCY } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, savePayrollRates, type RosterPayroll } from "../services/expensesService";
import { money } from "../lib/format";
import { ui } from "../theme";
import { Notice } from "./ui";

// Nómina del equipo estimada desde el padrón: el total mensual por moneda y el
// tipo de cambio con el que las monedas extranjeras entran al total en pesos.
// Solo muestra totales: el sueldo de cada persona se queda en Equipo.

export default function RosterPayrollCard({ roster, onSaved }: { roster: RosterPayroll; onSaved: () => void }) {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const foreign = Object.keys(roster.monthlyByCurrency).filter((cur) => cur !== BASE_CURRENCY);
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    Object.fromEntries(foreign.map((cur) => [cur, roster.rates[cur] ? String(roster.rates[cur].rate) : ""])),
  );
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: "ok" | "warn"; text: string } | null>(null);

  const monthlyBase = Object.entries(roster.monthlyByCurrency).reduce<number | null>((sum, [cur, amount]) => {
    if (sum == null) return null;
    const rate = cur === BASE_CURRENCY ? 1 : roster.rates[cur]?.rate;
    return rate ? sum + amount * rate : null;
  }, 0);

  async function save() {
    setSaving(true);
    setMessage(null);
    try {
      const payload = Object.fromEntries(foreign.map((cur) => [cur, draft[cur] ? Number(draft[cur]) : null]));
      await savePayrollRates(payload);
      setMessage({ tone: "ok", text: t("expenses.summary.payrollRateSaved") });
      onSaved();
    } catch (err) {
      setMessage({ tone: "warn", text: apiErrorMessage(err) || t("expenses.errors.load") });
    } finally {
      setSaving(false);
    }
  }

  if (roster.members === 0) {
    return (
      <Notice tone="warn">
        {t("expenses.summary.payrollEmpty")}
        {roster.withoutSalary > 0 && <> {t("expenses.summary.payrollWithoutSalary", { count: roster.withoutSalary })}</>}
      </Notice>
    );
  }

  return (
    <div className={c.card}>
      <h3 className={`mb-1 text-sm font-semibold ${c.text}`}>{t("expenses.summary.payrollTitle")}</h3>
      <p className={`text-xs ${c.muted}`}>{t("expenses.summary.payrollMonthly", { count: roster.members })}</p>
      <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        {Object.entries(roster.monthlyByCurrency).map(([cur, amount]) => (
          <span key={cur} className={`text-lg font-semibold tabular-nums ${c.text}`}>
            {money(amount, cur, i18n.language)}
          </span>
        ))}
        {foreign.length > 0 && monthlyBase != null && (
          <span className={`text-sm tabular-nums ${c.muted}`}>≈ {money(monthlyBase, BASE_CURRENCY, i18n.language)}</span>
        )}
      </div>
      <p className={`mt-2 text-xs ${c.muted}`}>{t("expenses.summary.payrollHint")}</p>

      {foreign.length > 0 && (
        <div className="mt-3 flex flex-wrap items-end gap-3">
          {foreign.map((cur) => {
            const r = roster.rates[cur];
            return (
              <label key={cur} className={`text-xs ${c.muted}`}>
                <span className="block">
                  {t("expenses.summary.payrollRate", { currency: cur })}
                  {r && <> · {t(`expenses.summary.payrollRateFrom_${r.source}`, { date: r.date || "" })}</>}
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  inputMode="decimal"
                  aria-label={t("expenses.summary.payrollRate", { currency: cur })}
                  className={`${c.inputBase} mt-1 w-32`}
                  value={draft[cur] ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, [cur]: e.target.value }))}
                />
              </label>
            );
          })}
          <button type="button" className={c.secondary} disabled={saving} onClick={save}>
            {t("expenses.summary.payrollRateSave")}
          </button>
          <p className={`w-full text-xs ${c.muted}`}>{t("expenses.summary.payrollRateAutoHint")}</p>
        </div>
      )}
      <div className="mt-3 space-y-2">
        {foreign
          .filter((cur) => !roster.rates[cur])
          .map((cur) => (
            <Notice key={cur} tone="warn">
              {t("expenses.summary.payrollRateMissing", { currency: cur })}
            </Notice>
          ))}
        {roster.withoutSalary > 0 && <Notice tone="warn">{t("expenses.summary.payrollWithoutSalary", { count: roster.withoutSalary })}</Notice>}
        {message && <Notice tone={message.tone}>{message.text}</Notice>}
      </div>
    </div>
  );
}
